import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtCurrency } from "@/lib/api";
import { Plus, Trash2, Vault, Info, ChevronDown, ChevronRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

// -----------------------------------------------------------------------------
// ReservesMatrix — balance-sheet reserves per club, tabulated by historical year.
// Renders directly below HistoricalsMatrix. Each reserve gets 4 rows:
//   • Beginsaldo   (editable only in the first year with data; auto-chained else)
//   • + Dotatie    (deposit from that year's result)
//   • − Onttrekking (withdrawal to that year's result)
//   • = Eindsaldo  (computed on the server; read-only, bold)
//
// The scenario builder consumes latest_closing_balance from this endpoint when
// the user marks a future investment as "funded from reservation".
// -----------------------------------------------------------------------------

export default function ReservesMatrix({ t, clubId, historicals, locked = false, onChanged }) {
  const [reserves, setReserves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const years = useMemo(
    () => (historicals || []).slice().sort((a, b) => a.year - b.year),
    [historicals]
  );

  const load = async () => {
    if (!clubId) return;
    try {
      const { data } = await api.get(`/clubs/${clubId}/reserves`);
      setReserves(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clubId, historicals]);

  // Uitklapstand per reserve-id; blijft bewaard als de lijst na een save opnieuw laadt.
  const [openIds, setOpenIds] = useState({});
  const toggleOpen = (id) => setOpenIds((m) => ({ ...m, [id]: !m[id] }));

  const [showEmpty, setShowEmpty] = useState(false);
  // Een reserve is "leeg" als er in geen enkel boekjaar een beweging of saldo staat.
  const isEmptyReserve = (r) => !years.some((y) => {
    const m = (y.reserves_breakdown || []).find((x) => x.name === r.name);
    return m && (Number(m.opening_balance) || Number(m.dotatie) || Number(m.onttrekking) || Number(m.closing_balance));
  });
  const emptyCount = reserves.filter(isEmptyReserve).length;
  const visibleReserves = showEmpty ? reserves : reserves.filter((r) => !isEmptyReserve(r));

  const addReserve = async () => {
    const name = newName.trim();
    if (!name) return;
    if (reserves.some((r) => r.name.trim().toLowerCase() === name.toLowerCase())) {
      toast.error(t("Er bestaat al een reserve met de naam '{name}'.").replace("{name}", name));
      return;
    }
    try {
      await api.post(`/clubs/${clubId}/reserves`, { name, category: "earmarked" });
      setNewName("");
      setAddOpen(false);
      setShowEmpty(true);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const renameReserve = async (r, name) => {
    const trimmed = (name || "").trim();
    if (!trimmed || trimmed === r.name) return;
    try {
      await api.put(`/reserves/${r.id}`, { name: trimmed, category: r.category });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteReserve = async (r) => {
    if (!window.confirm(t("Weet je zeker dat je '{name}' wilt verwijderen? Alle bewegingen in de historische jaren gaan verloren.").replace("{name}", r.name))) return;
    try {
      await api.delete(`/reserves/${r.id}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // Per-year movement lookup for a given reserve name.
  const getMovement = (year, reserveName) => {
    const rb = (year.reserves_breakdown || []).find((m) => m.name === reserveName);
    return rb || { opening_balance: 0, dotatie: 0, onttrekking: 0, closing_balance: 0 };
  };

  const isFirstYearForReserve = (yearIdx, reserveName) => {
    // Editable opening only when this is the first year in which the reserve has a movement row.
    for (let i = 0; i < yearIdx; i++) {
      const m = (years[i].reserves_breakdown || []).find((x) => x.name === reserveName);
      if (m) return false;
    }
    return true;
  };

  const saveMovement = async (year, reserveName, field, value) => {
    // Nieuwe reserves_breakdown voor dit jaar (kopieën, de prop zelf blijft onaangeroerd).
    const val = Number(value) || 0;
    const bd = (year.reserves_breakdown || []).map((x) => ({ ...x }));
    let target = bd.find((x) => x.name === reserveName);
    if (!target) {
      target = { name: reserveName, opening_balance: 0, dotatie: 0, onttrekking: 0, closing_balance: 0 };
      bd.push(target);
    }
    target[field] = val;
    // Send the FULL historical row back (server preserves other fields).
    const payload = {
      year: year.year,
      revenue: year.revenue || 0,
      operating_costs: year.operating_costs || 0,
      depreciation: year.depreciation || 0,
      interest_paid: year.interest_paid || 0,
      other_income: year.other_income || 0,
      balance: year.balance || 0,
      cash_bank_balance: year.cash_bank_balance || 0,
      revenue_breakdown: year.revenue_breakdown || [],
      opex_breakdown: year.opex_breakdown || [],
      other_income_breakdown: year.other_income_breakdown || [],
      reserves_breakdown: bd,
    };
    try {
      await api.put(`/historicals/${year.id}`, payload);
      // ClubPage haalt de historicals opnieuw op (eindsaldi/ketting), wij de reserves (laatste saldo).
      if (onChanged) await onChanged(); else await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!clubId) return null;

  return (
    <section className="mt-6 border-2 border-foreground bg-card heavy-shadow" data-testid="reserves-matrix">
      <header className="flex items-center justify-between p-4 border-b-2 border-foreground bg-accent/20">
        <div>
          <div className="flex items-center gap-2">
            <Vault className="w-5 h-5" />
            <h3 className="font-display uppercase font-black text-xl tracking-tight">{t("Reserves en voorzieningen")}</h3>
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            {t("Algemene reserve, bestemmingsreserves en voorzieningen (bv. voorziening groot onderhoud) per boekjaar. Bewegingen (dotatie / onttrekking) worden per jaar bijgehouden; eindsaldi zijn de basis voor 'financiering uit reservering' in scenario's.")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
        {emptyCount > 0 && (
          <button type="button" onClick={() => setShowEmpty((v) => !v)} data-testid="reserves-toggle-empty"
            title={showEmpty ? t("Verberg reserves zonder bedragen") : t("Toon reserves zonder bedragen")}
            className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-1">
            {showEmpty ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showEmpty ? t("Verberg lege ({n})").replace("{n}", String(emptyCount)) : t("Toon lege ({n})").replace("{n}", String(emptyCount))}
          </button>
        )}
        <button
          disabled={locked}
          onClick={() => setAddOpen((v) => !v)}
          data-testid="reserves-add-btn"
          className={`btn-brutal px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-1 ${locked ? "opacity-40 cursor-not-allowed bg-muted" : "bg-primary text-primary-foreground"}`}
        >
          <Plus className="w-4 h-4" /> {t("Reserve / voorziening")}
        </button>
        </div>
      </header>

      {addOpen && !locked && (
        <div className="p-3 bg-muted/30 border-b border-border flex items-center gap-2" data-testid="reserves-add-row">
          <input
            autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addReserve(); }}
            placeholder={t("Naam, bv. 'Vervanging steigers' of 'Voorziening groot onderhoud'")}
            data-testid="reserves-new-name"
            className="flex-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button onClick={addReserve} data-testid="reserves-save-new"
            className="btn-brutal bg-primary text-primary-foreground px-3 py-2 font-display uppercase font-bold text-sm">
            {t("Toevoegen")}
          </button>
          <button onClick={() => { setAddOpen(false); setNewName(""); }}
            className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-sm">
            {t("Annuleren")}
          </button>
        </div>
      )}

      {loading ? (
        <div className="p-4 text-sm text-muted-foreground">{t("Laden…")}</div>
      ) : reserves.length === 0 || years.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5" />
          {years.length === 0
            ? t("Voeg eerst historische jaren toe om reserves te tabuleren.")
            : t("Nog geen reserves gedefinieerd — de algemene reserve is automatisch aangemaakt.")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40">
                <th className="text-left px-4 py-2 font-display uppercase tracking-widest text-xs sticky left-0 bg-muted/40 z-10 border-r-2 border-foreground/20 min-w-[220px]">
                  {t("Reserve / bewegingstype")}
                </th>
                {years.map((y) => (
                  <th key={y.id} className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs font-black">{y.year}</th>
                ))}
                <th className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs font-black text-primary">
                  {t("Nu")}
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleReserves.length === 0 && (
                <tr><td colSpan={years.length + 2} className="px-4 py-3 text-xs text-muted-foreground" data-testid="reserves-all-empty">{t("Alle reserves zijn nog leeg — klik op 'Toon lege' om bedragen in te vullen.")}</td></tr>
              )}
              {visibleReserves.map((r) => (
                <ReserveGroup
                  key={r.id}
                  t={t} r={r} years={years} locked={locked}
                  open={Boolean(openIds[r.id])} onToggle={() => toggleOpen(r.id)}
                  getMovement={getMovement}
                  isFirstYearForReserve={isFirstYearForReserve}
                  saveMovement={saveMovement}
                  onRename={renameReserve}
                  onDelete={deleteReserve}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


function ReserveGroup({ t, r, years, locked, open, onToggle, getMovement, isFirstYearForReserve, saveMovement, onRename, onDelete }) {
  const [editName, setEditName] = useState(false);
  const [name, setName] = useState(r.name);

  useEffect(() => setName(r.name), [r.name]);

  const commitName = () => {
    setEditName(false);
    if (name !== r.name) onRename(r, name);
  };

  return (
    <>
      {/* Samengevouwen regel: naam + eindsaldo per jaar; uitklappen toont begin/dotatie/onttrekking */}
      <tr className="bg-foreground/5 border-t-4 border-foreground/20" data-testid={`reserve-group-${r.id}`}>
        <td className="px-2 py-2 sticky left-0 bg-foreground/5 z-10 border-r-2 border-foreground/20">
          <div className="flex items-center gap-2 group">
            <button type="button" onClick={onToggle} data-testid={`reserve-toggle-${r.id}`} aria-expanded={open}
              title={open ? t("Bewegingen verbergen") : t("Bewegingen tonen (beginsaldo, dotatie, onttrekking)")}
              className="p-0.5 text-muted-foreground hover:text-foreground">
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <span className={`text-[10px] px-1.5 py-0.5 font-bold uppercase tracking-widest ${r.category === "general" ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground"}`}>
              {r.category === "general" ? t("Algemeen") : t("Bestemming")}
            </span>
            {editName ? (
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
                     onBlur={commitName}
                     onKeyDown={(e) => { if (e.key === "Enter") commitName(); if (e.key === "Escape") { setEditName(false); setName(r.name); } }}
                     className="font-display uppercase font-black text-sm tracking-tight border-b-2 border-primary bg-transparent px-1 focus:outline-none flex-1 max-w-md"
                     data-testid={`reserve-rename-input-${r.id}`}
              />
            ) : (
              <button disabled={locked} onClick={() => setEditName(true)}
                      className={`font-display uppercase font-black text-sm tracking-tight truncate ${locked ? "cursor-default" : "hover:underline"}`}
                      data-testid={`reserve-name-${r.id}`}>
                {r.name}
              </button>
            )}
            <div className="flex-1" />
            {!r.is_system && !locked && (
              <button onClick={() => onDelete(r)} data-testid={`reserve-delete-${r.id}`}
                      className="p-1 text-muted-foreground hover:text-destructive rounded transition-colors"
                      title={t("Reserve verwijderen")}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
        {years.map((y) => {
          const m = getMovement(y, r.name);
          return (
            <td key={y.id} className="px-3 py-2 text-right font-mono-num text-xs font-bold" data-testid={`reserve-closing-${r.id}-${y.year}`}>
              {Number(m.closing_balance) ? fmtCurrency(m.closing_balance) : "—"}
            </td>
          );
        })}
        <td className="px-3 py-2 text-right">
          <span className="font-mono-num text-primary font-bold text-xs" data-testid={`reserve-latest-${r.id}`}>
            {r.latest_closing_balance != null ? fmtCurrency(r.latest_closing_balance) : "—"}
          </span>
        </td>
      </tr>
      {open && (
        <>
          <MovementRow
            label={t("Beginsaldo")} r={r} years={years} field="opening_balance"
            subtle isEditable={(y, idx) => !locked && isFirstYearForReserve(idx, r.name)}
            getMovement={getMovement} saveMovement={saveMovement}
          />
          <MovementRow
            label={t("+ Dotatie")} r={r} years={years} field="dotatie"
            isEditable={() => !locked}
            getMovement={getMovement} saveMovement={saveMovement}
          />
          <MovementRow
            label={t("− Onttrekking")} r={r} years={years} field="onttrekking"
            isEditable={() => !locked}
            getMovement={getMovement} saveMovement={saveMovement}
          />
          <MovementRow
            label={t("= Eindsaldo")} r={r} years={years} field="closing_balance"
            strong isEditable={() => false}
            getMovement={getMovement} saveMovement={saveMovement}
          />
        </>
      )}
    </>
  );
}


function MovementRow({ label, r, years, field, subtle = false, strong = false, isEditable, getMovement, saveMovement, latest }) {
  return (
    <tr className="hover:bg-muted/20">
      <td className={`px-4 py-1.5 sticky left-0 bg-card z-10 border-r-2 border-foreground/20 text-xs ${strong ? "font-display uppercase font-bold" : subtle ? "text-muted-foreground pl-8" : "pl-8"}`}>
        {label}
      </td>
      {years.map((y, idx) => {
        const m = getMovement(y, r.name);
        const editable = isEditable(y, idx);
        return (
          <td key={y.id} className="px-2 py-0.5 text-right">
            <ReserveCell
              initial={m[field] || 0}
              readOnly={!editable}
              subtle={subtle && !editable}
              strong={strong}
              onCommit={(v) => saveMovement(y, r.name, field, v)}
              testid={`reserve-cell-${r.id}-${field}-${y.year}`}
            />
          </td>
        );
      })}
      <td className="px-3 py-0.5 text-right">
        {field === "closing_balance" && latest != null ? <span className="font-mono-num text-primary/60 text-xs">{fmtCurrency(latest)}</span> : ""}
      </td>
    </tr>
  );
}


function ReserveCell({ initial, onCommit, testid, subtle, strong, readOnly }) {
  const [val, setVal] = useState(initial ?? 0);
  useEffect(() => { setVal(initial ?? 0); }, [initial]);
  if (readOnly) {
    return (
      <span className={`font-mono-num text-xs px-1 ${subtle ? "text-muted-foreground/50" : strong ? "font-bold" : ""}`} data-testid={testid}>
        {Number(val) ? fmtCurrency(val) : "—"}
      </span>
    );
  }
  return (
    <input
      type="number"
      value={val === 0 ? "" : val}
      placeholder="0"
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { const n = Number(val) || 0; if (n !== (Number(initial) || 0)) onCommit(n); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      data-testid={testid}
      className="w-full max-w-[110px] text-right font-mono-num bg-transparent px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary focus:bg-background text-xs"
    />
  );
}
